/**
 * AiIdeaCard — one idea from the nightly board, action first.
 *
 * WHY THIS EXISTS
 *
 * The previous card printed up to TEN blocks of model prose at once: the summary, a bordered
 * "Beyond RockBand" panel, three reasons for, three reasons against, the key uncertainty, the
 * invalidation comment and the redundancy note — all untruncated, two cards to a row. Measured on
 * the live board that was 2,506 words on the Signals tab for five ideas. Nobody reads that daily,
 * and the one thing it never said was what to DO.
 *
 * So the default state answers the reader's actual question — what is the action, and what would
 * change it — and every word of the reasoning moves behind one "Why" disclosure. Nothing is
 * deleted: the detail is the reason this layer exists, it just stops being the opening sentence.
 *
 * `decisionChangingEvent` was stored on every assessment and rendered nowhere. It is the field
 * that literally answers "what would make this actionable", so it now leads the card.
 */
import React, { useState } from 'react';
import { Box, Typography, Chip, Button, Divider, Tooltip, Collapse } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate } from 'react-router-dom';
import { useAppTheme } from '../../hooks/useAppTheme';
import type { AiCandidate } from '../../services/ai.service';

/** What the state means as a label. Shared with the rest of the desk. */
export const STATE_LABEL: Record<string, string> = {
  BUY_NOW: 'Act now',
  BUY_ON_PULLBACK: 'On pullback',
  WAIT_FOR_CONFIRMATION: 'Wait for confirmation',
  WATCH: 'Watch',
  AVOID: 'Avoid',
};

/**
 * The action line — state x direction turned into an instruction rather than a label.
 *
 * "On pullback" is not an action; "wait for a pullback, then buy" is. The direction matters
 * because a pullback on a SHORT is a bounce, and calling both "a pullback" is how a reader ends up
 * on the wrong side of a trade they read correctly.
 */
export function actionLine(state: string | null, direction: string | null): string {
  const short = direction === 'SHORT';
  switch (state) {
    case 'BUY_NOW':
      return short ? 'Short now' : 'Buy now';
    case 'BUY_ON_PULLBACK':
      return short ? 'Wait for a bounce, then short' : 'Wait for a pullback, then buy';
    case 'WAIT_FOR_CONFIRMATION':
      return short ? 'Not yet — wait for the breakdown to confirm' : 'Not yet — wait for confirmation';
    case 'WATCH':
      return 'Watch only — no trade here yet';
    case 'AVOID':
      return 'Skip this one';
    default:
      return 'No call — RockBand signal only';
  }
}

/** Colour for the action line. Only an outright entry earns the bullish/bearish colour. */
function actionColor(state: string | null, direction: string | null, C: any): string {
  if (state === 'AVOID') return C.bear;
  if (state === 'BUY_NOW') return direction === 'SHORT' ? C.bear : C.bull;
  if (state === 'BUY_ON_PULLBACK') return C.neutral;
  return C.warn;
}

interface Props {
  c: AiCandidate;
  /** Other symbols in this candidate's correlation cluster, already filtered of the symbol itself. */
  peers: string[];
  gradeColor: (g: string | null) => string;
  /** RockBand's two reads, rendered by the caller so the chip stays defined in one place. */
  renderRbChips: (c: AiCandidate) => React.ReactNode;
  onAct: (c: AiCandidate, action: 'WATCHED' | 'DISMISSED') => void;
}

const AiIdeaCard: React.FC<Props> = ({ c, peers, gradeColor, renderRbChips, onAct }) => {
  const { C } = useAppTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const p: any = c.assessment || {};

  const act = actionLine(c.state, c.direction);
  const actCol = actionColor(c.state, c.direction, C);

  /** The trigger that would change the answer. Falls back to the invalidation when the model
   *  offered no forward trigger, because "what breaks it" is still more use than nothing. */
  const trigger: string | null = p.decisionChangingEvent || p.invalidationComment || null;

  /** How much the "Why" block is holding, so the button can say so before it is pressed. */
  const detailCount =
    (c.summary ? 1 : 0) + (p.addsBeyondSignal && p.restatesSignal !== true ? 1 : 0) +
    (p.reasonsFor?.length || 0) + (p.reasonsAgainst?.length || 0) +
    (p.keyUncertainty ? 1 : 0) + (p.invalidationComment ? 1 : 0) + (p.redundancyNote ? 1 : 0);

  return (
    <Box sx={{
      p: 1.5, borderRadius: 1, border: `1px solid ${gradeColor(c.grade)}55`,
      bgcolor: C.neutralMuted, opacity: c.humanAction === 'DISMISSED' ? 0.5 : 1,
      display: 'flex', flexDirection: 'column',
    }}>
      {/* ── Identity ──────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
        <Typography sx={{ fontWeight: 800, fontSize: '1rem' }}>#{c.finalRank} {c.symbol}</Typography>
        <Tooltip arrow title="The model's confidence grade for this idea, A (best) to D. C or D is a normal result — the rubric says so — and the grade is what the Performance tab measures outcomes against.">
          <Chip size="small" label={c.grade || '—'} sx={{
            height: 20, fontWeight: 800,
            bgcolor: `${gradeColor(c.grade)}22`, color: gradeColor(c.grade),
            border: `1px solid ${gradeColor(c.grade)}55`,
          }} />
        </Tooltip>
        {c.direction && c.direction !== 'NONE' && (
          <Chip size="small" label={c.direction} sx={{
            height: 20, fontWeight: 700,
            bgcolor: c.direction === 'LONG' ? C.bullMuted : C.bearMuted,
            color: c.direction === 'LONG' ? C.bull : C.bear,
          }} />
        )}
        {c.conviction != null && (
          <Tooltip arrow title="The model's own confidence in this call, 0-100. It is not a probability and has not been calibrated against outcomes — read it as a ranking within the board, not as odds.">
            <Typography variant="caption" sx={{ color: C.dim, cursor: 'help' }}>conviction {c.conviction}</Typography>
          </Tooltip>
        )}
        <Box sx={{ flex: 1 }} />
        <Tooltip arrow title="Open chart">
          <Button size="small" sx={{ minWidth: 0, px: 0.7 }}
            onClick={() => navigate(`/charts/${encodeURIComponent(c.symbol)}?exchange=${encodeURIComponent(c.exchange)}`)}>
            <CandlestickChartIcon fontSize="small" />
          </Button>
        </Tooltip>
      </Box>

      {/* ── The action, and what would change it ──────────────────────── */}
      <Typography sx={{ mt: 1, fontWeight: 800, fontSize: '0.95rem', color: actCol, lineHeight: 1.3 }}>
        {act}
      </Typography>
      {trigger && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.4, lineHeight: 1.45 }}>
          <b style={{ color: C.dim }}>What changes it: </b>{trigger}
        </Typography>
      )}

      {/* One-glance context: RockBand's two reads, beta, and whether this stands in for peers. */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
        {renderRbChips(c)}
        {c.btcBeta != null && (
          <Tooltip arrow title="Beta to Bitcoin over 90 days. Above 1 means this amplifies the same move; a basket of high-beta longs is one leveraged bet, not several.">
            <Chip size="small" label={`BTC β ${Number(c.btcBeta).toFixed(2)}`}
              sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700 }} />
          </Tooltip>
        )}
        {!!peers.length && (
          <Tooltip arrow title={`Same bet as: ${peers.join(', ')}. One representative per cluster reaches the model, so these were not assessed separately.`}>
            <Chip size="small" label={`stands in for ${peers.length}`}
              sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: C.warnMuted, color: C.warn }} />
          </Tooltip>
        )}
      </Box>

      {/* ── Everything else, on request ───────────────────────────────── */}
      {detailCount > 0 && (
        <>
          <Button size="small" variant="text" onClick={() => setOpen(v => !v)}
            startIcon={open ? <ExpandMoreIcon /> : <ChevronRightIcon />}
            sx={{ alignSelf: 'flex-start', mt: 0.8, px: 0.5, textTransform: 'none', fontWeight: 700 }}>
            {open ? 'Hide the reasoning' : `Why — the full reasoning (${detailCount})`}
          </Button>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ pt: 0.5 }}>
              {c.summary && (
                <Typography variant="body2" sx={{ lineHeight: 1.45 }}>{c.summary}</Typography>
              )}

              {/* What the model added that RockBand could not — the reason this layer exists. */}
              {p.addsBeyondSignal && p.restatesSignal !== true && (
                <Box sx={{ mt: 1, p: 1, borderRadius: 0.8, bgcolor: C.accentMuted, border: `1px solid ${C.accentBorder}` }}>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: C.accent, display: 'block' }}>
                    Beyond RockBand
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block' }}>{p.addsBeyondSignal}</Typography>
                </Box>
              )}

              {(!!p.reasonsFor?.length || !!p.reasonsAgainst?.length) && (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1, mt: 1 }}>
                  <Box>
                    {p.reasonsFor?.map((r: string, i: number) => (
                      <Typography key={i} variant="caption" sx={{ display: 'block', color: C.bull }}>+ {r}</Typography>
                    ))}
                  </Box>
                  <Box>
                    {p.reasonsAgainst?.map((r: string, i: number) => (
                      <Typography key={i} variant="caption" sx={{ display: 'block', color: C.bear }}>− {r}</Typography>
                    ))}
                  </Box>
                </Box>
              )}

              {p.keyUncertainty && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.8 }}>
                  <b>Main uncertainty:</b> {p.keyUncertainty}
                </Typography>
              )}
              {p.invalidationComment && (
                <Typography variant="caption" sx={{ display: 'block' }}>
                  <b>Invalidated if:</b> {p.invalidationComment}
                </Typography>
              )}
              {p.redundancyNote && (
                <Typography variant="caption" sx={{ display: 'block', color: C.dim, mt: 0.5 }}>
                  {p.redundancyNote}
                </Typography>
              )}
            </Box>
          </Collapse>
        </>
      )}

      <Box sx={{ flex: 1 }} />
      <Divider sx={{ my: 1 }} />
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button size="small" variant={c.humanAction === 'WATCHED' ? 'contained' : 'outlined'}
          startIcon={<VisibilityIcon />} onClick={() => onAct(c, 'WATCHED')}>
          Watch
        </Button>
        <Button size="small" color="inherit"
          variant={c.humanAction === 'DISMISSED' ? 'contained' : 'outlined'}
          startIcon={<CloseIcon />} onClick={() => onAct(c, 'DISMISSED')}>
          Dismiss
        </Button>
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" sx={{ color: C.dim }}>
          opinion only — stop and size come from the risk engine
        </Typography>
      </Box>
    </Box>
  );
};

export default AiIdeaCard;
